import {
  LucideContainer,
  LucideFileSpreadsheet,
  LucideSettings,
  LucideTruck,
  LucideUser2,
  LucideUsers,
  LucideBuilding,
  LucidePackage,
  LucideUserCheck,
  LucideReceipt,
  ChevronDown,
  ChevronRight,
  LucidePlus,
  LucideTrash,
  LucideUserPlus,
  LucidePackage2,
  LucideMapPin,
  LucideDollarSign,
  LucideClipboardList,
  LucidePercent,
  LucideSend,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Signout } from 'src/components/Signout';
import { OrganizationSelector } from 'src/components/OrganizationSelector';
import { useState, useEffect } from 'react';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
interface SubMenuItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface NavigationItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: SubMenuItem[];
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [pricingExpanded, setPricingExpanded] = useState(false);
  const [leadsMarketingExpanded, setLeadsMarketingExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isCleanupManagementEnabled = useFeatureFlag('cleanupsManagement');

  // Auto-expand menus if we're on their pages
  useEffect(() => {
    if (location.pathname.includes('/settings/')) {
      setSettingsExpanded(true);
    }
    if (location.pathname.startsWith('/pricing') || location.pathname.startsWith('/discounts')) {
      setPricingExpanded(true);
    }
    if (location.pathname.startsWith('/leads')) {
      setLeadsMarketingExpanded(true);
    }
  }, [location.pathname]);

  const navigationItems: NavigationItem[] = [
    {
      label: 'Orders',
      icon: <LucideFileSpreadsheet className="h-5 w-5" />,
      path: '/orders',
    },
    {
      label: 'Invoices',
      icon: <LucideReceipt className="h-5 w-5" />,
      path: '/invoices',
    },
    {
      label: 'Customers',
      icon: <LucideUsers className="h-5 w-5" />,
      path: '/customers',
    },
    {
      label: 'Leads & Marketing',
      icon: <LucideUserPlus className="h-5 w-5" />,
      subItems: [
        {
          label: 'Leads',
          icon: <LucideUsers className="h-4 w-4" />,
          path: '/leads?tab=leads',
        },
        {
          label: 'Marketing',
          icon: <LucideSend className="h-4 w-4" />,
          path: '/leads?tab=marketing',
        },
      ],
    },
    {
      label: 'Drivers',
      icon: <LucideUser2 className="h-5 w-5" />,
      path: '/drivers',
    },
    {
      label: 'Assets',
      icon: <LucideContainer className="h-5 w-5" />,
      path: '/dumpsters',
    },
    {
      label: 'Trucks',
      icon: <LucideTruck className="h-5 w-5" />,
      path: '/trucks',
    },
    {
      label: 'Pricing',
      icon: <LucideDollarSign className="h-5 w-5" />,
      subItems: [
        {
          label: 'Rules',
          icon: <LucideClipboardList className="h-4 w-4" />,
          path: '/pricing/rules',
        },
        {
          label: 'Discounts',
          icon: <LucidePercent className="h-4 w-4" />,
          path: '/pricing/discounts',
        },
        {
          label: 'City Fees',
          icon: <LucideMapPin className="h-4 w-4" />,
          path: '/pricing/city-fees',
        },
      ],
    },
    {
      label: 'Organizations',
      icon: <LucideBuilding className="h-5 w-5" />,
      path: '/organizations',
    },
    {
      label: 'Settings',
      icon: <LucideSettings className="h-5 w-5" />,
      subItems: [
        {
          label: 'Add-ons',
          icon: <LucidePackage className="h-4 w-4" />,
          path: '/settings/add-ons',
        },

        {
          label: 'Products',
          icon: <LucidePlus className="h-4 w-4" />,
          path: '/settings/products',
        },

        {
          label: 'Users',
          icon: <LucideUserCheck className="h-4 w-4" />,
          path: '/settings/users',
        },
        {
          label: 'Dumpster Types',
          icon: <LucideContainer className="h-4 w-4" />,
          path: '/settings/container-types',
        },
        {
          label: 'Materials',
          icon: <LucidePackage2 className="h-4 w-4" />,
          path: '/settings/materials',
        },
        {
          label: 'Locations',
          icon: <LucideMapPin className="h-4 w-4" />,
          path: '/settings/locations',
        },
        ...(isCleanupManagementEnabled
          ? [
              {
                label: 'Cleanups',
                icon: <LucideTrash className="h-4 w-4" />,
                path: '/settings/cleanups',
              },
            ]
          : []),
      ],
    },
  ];

  const handleNavigationClick = (item: NavigationItem) => {
    if (item.subItems) {
      if (item.label === 'Settings') {
        setSettingsExpanded(!settingsExpanded);
      } else if (item.label === 'Pricing') {
        setPricingExpanded(!pricingExpanded);
      } else if (item.label === 'Leads & Marketing') {
        setLeadsMarketingExpanded(!leadsMarketingExpanded);
      }
    } else if (item.path) {
      navigate(item.path);
      // Auto-close sidebar on mobile after navigation
      if (window.innerWidth < 1024) {
        onClose();
      }
    }
  };

  const handleSubItemClick = (subItem: SubMenuItem) => {
    navigate(subItem.path);
    // Auto-close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const isActiveRoute = (path: string) => {
    if (path.includes('?')) {
      const [pathname, queryString] = path.split('?');
      if (location.pathname !== pathname) return false;
      const pathParams = new URLSearchParams(queryString);
      const currentParams = new URLSearchParams(location.search);
      for (const [key, value] of pathParams.entries()) {
        const currentValue = currentParams.get(key);
        // When no tab param is present, default to 'leads' tab
        if (key === 'tab' && currentValue === null && value === 'leads') continue;
        if (currentValue !== value) return false;
      }
      return true;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isSettingsActive = () => {
    return location.pathname.startsWith('/settings');
  };

  const isPricingActive = () => {
    return location.pathname.startsWith('/pricing') || location.pathname.startsWith('/discounts');
  };

  const isLeadsMarketingActive = () => {
    return location.pathname.startsWith('/leads');
  };

  return (
    <div
      className={`${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 
      transition-transform duration-300 ease-in-out lg:transition-none flex flex-col`}
    >
      {/* Logo and Organization Selector */}
      <div className="flex-shrink-0 p-6 border-b border-gray-200">
        <img src="/up-waste-logo.png" alt="Up Waste Logo" className="h-8 w-auto mx-auto lg:mx-0" />
        <div className="mt-4">
          <OrganizationSelector />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-4 py-6">
        <div className="space-y-4">
          {navigationItems.map((item) => (
            <div key={item.label}>
              <button
                onClick={() => handleNavigationClick(item)}
                className={`w-full flex items-center justify-between px-3 py-2 text-base font-medium rounded-md transition-colors ${
                  item.path && isActiveRoute(item.path)
                    ? 'bg-primary/10 text-primary border-r-2 border-primary'
                    : item.subItems && item.label === 'Settings' && isSettingsActive()
                      ? 'bg-gray-100 text-gray-900'
                      : item.subItems && item.label === 'Pricing' && isPricingActive()
                        ? 'bg-gray-100 text-gray-900'
                        : item.subItems && item.label === 'Leads & Marketing' && isLeadsMarketingActive()
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-3">
                  {item.icon}
                  <span>{item.label}</span>
                </div>
                {item.subItems && (
                  <div className="ml-auto">
                    {(item.label === 'Settings' && settingsExpanded) ||
                    (item.label === 'Pricing' && pricingExpanded) ||
                    (item.label === 'Leads & Marketing' && leadsMarketingExpanded) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </div>
                )}
              </button>

              {/* Sub-items */}
              {item.subItems &&
                ((item.label === 'Settings' && settingsExpanded) ||
                  (item.label === 'Pricing' && pricingExpanded) ||
                  (item.label === 'Leads & Marketing' && leadsMarketingExpanded)) && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.subItems.map((subItem) => (
                      <button
                        key={subItem.label}
                        onClick={() => handleSubItemClick(subItem)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-base font-medium rounded-md transition-colors ${
                          isActiveRoute(subItem.path)
                            ? 'bg-primary/10 text-primary border-r-2 border-primary'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        {subItem.icon}
                        <span>{subItem.label}</span>
                      </button>
                    ))}
                  </div>
                )}
            </div>
          ))}
        </div>
      </nav>

      {/* Sign out */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200">
        <Signout />
      </div>
    </div>
  );
};
